import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { scoreTranslation } from '@treams/treams-analytics/src/score/services/ScoreConfig';
import { InfoAlternateIcon } from '@treams/treams-icons';
import {
	Card,
	Drawer,
	GaugeMeter,
	Heading3,
	IconColor,
	Paragraph,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import type {
	ScoreCardFactors,
	ScoreColors,
	ScoreFactor,
	TreamsCategoryScore,
} from '../TreamsScore.types';
import { SCORE_CATEGORY_ORDER } from '../TreamsScoreTable/columns';
import { SCORE_WEIGHTS } from '../config/score-weights';
import { toDisplayScore } from '../scoreDisplay';
import ScoreBreakDown from './ScoreBreakDown';
import TreamsScoreExplanation from './TreamsScoreExplanation';

interface IProps {
	factors: ScoreCardFactors;
	total: TreamsCategoryScore;
	getScoreColors: (score: number) => ScoreColors;
	engagementDaysRemaining: number | null;
}

export function TreamsScoreCard({
	factors,
	total,
	getScoreColors,
	engagementDaysRemaining,
}: IProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const orderedFactors = useMemo<ScoreFactor[]>(
		() =>
			SCORE_CATEGORY_ORDER.map((key) => ({
				category: key,
				label: factors[key].label,
				score: factors[key].score,
				weight: Math.round(SCORE_WEIGHTS[key] * 100),
				state: factors[key].state,
			})),
		[factors],
	);

	return (
		<>
			<Drawer
				onClose={() => setOpen(false)}
				open={open}
				ariaLabel={t('analytics.treamsScore.explanation.title')}
				closeLabel={t('common.close')}
			>
				<TreamsScoreExplanation factors={orderedFactors} />
			</Drawer>
			<Card
				style={{
					overflow: 'visible',
				}}
			>
				<Heading3 mb={2}>{t('analytics.treamsScore.card.heading')}</Heading3>
				<Paragraph>{t('analytics.treamsScore.card.description')}</Paragraph>
				<Flex
					my={4}
					flexDirection={['column', 'column', 'row']}
					alignItems={['center', 'center', 'stretch']}
					style={{
						flexGrow: 1,
					}}
				>
					<Box
						px={5}
						width={['100%', '100%', '350px']}
						style={{
							flexShrink: 0,
							display: 'flex',
							justifyItems: 'center',
							justifyContent: 'center',
						}}
					>
						<GaugeMeter
							size={290}
							value={toDisplayScore(total.score)}
							delta={total.delta}
							label={t(scoreTranslation(toDisplayScore(total.score)))}
							format={{ maximumFractionDigits: 1 }}
						/>
					</Box>

					<Box mt={[4, 4, 0]} width="100%" style={{ flexGrow: 1, minWidth: 0 }}>
						<Box display="flex" justifyContent="space-between">
							<Subheading>{t('analytics.treamsScore.card.breakdownTitle')}</Subheading>
							<button
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: '4px',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									color: tokens.text.link,
									textDecoration: 'underline',
								}}
								onClick={() => setOpen(true)}
							>
								{t('analytics.treamsScore.explanation.title')}
								<IconColor color="slateBlue">
									<InfoAlternateIcon style={{ width: '16px', height: '16px' }} />
								</IconColor>
							</button>
						</Box>
						<ScoreBreakDown
							factors={orderedFactors}
							getScoreColors={getScoreColors}
							engagementDaysRemaining={engagementDaysRemaining}
						/>
					</Box>
				</Flex>
			</Card>
		</>
	);
}
